/**
 * «Предложенные темы» (#845, PR 5b; спека docs/plans/2026-10-02-conversation-themes.md
 * §4) — блок панели набора тем для тех, кто его правит.
 *
 * Раз в неделю сводка собирает из разговоров последних 30 дней предметы,
 * которых в наборе нет, — по ним и показаны предложения: название, определение
 * (или «уточнить» — оно не пережило минимизацию), число разговоров и доля
 * периода (считает SQL, не модель), 2–3 примера-разговора в охвате раздела.
 * «Принять» добавляет тему в набор (только с этого нажатия её определение идёт в
 * промпт), «Отклонить» — ключ молчит 90 дней. Предложение в `accepting`
 * (нажатие не завершилось) показано «Добавляется…»: повторное «Принять»
 * доводит его.
 *
 * Пример открывается тем же путём, что разговоры остального раздела
 * (`onOpenCall`: портал и встройка Bitrix24 открывают запись по-своему).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgThemeProposal } from '@aica/api-types/schemas';
import { Button } from '@/components/ui/button';
import { formatDateLocale, resolveLocale } from '@/lib/utils';
import { proposalErrorMessage } from './proposal-error-message';
import {
  useDecideThemeProposal,
  useThemeProposals,
  type ThemeProposalDecision,
} from './useThemeProposals';

export interface VoiceThemeProposalsProps {
  /** Open one example conversation; absent — examples are listed without links. */
  onOpenCall?: (recordId: string) => void;
}

/**
 * Доля периода — проценты с одним знаком, по правилам языка интерфейса («3,4»
 * в ru/uk/es, «3.4» в en): порог Р3 — 3 %, «3» и «3,4» различаются.
 */
function sharePercent(share: number): string {
  return new Intl.NumberFormat(resolveLocale(), { maximumFractionDigits: 1 }).format(
    Math.round(share * 1000) / 10
  );
}

/**
 * Окно предложения — [начало, конец): конец не входит. Показываем последний
 * ВКЛЮЧЁННЫЙ день — день момента на миллисекунду раньше, в той же зоне, в
 * какой показано всё остальное.
 */
function lastIncludedDay(windowEnd: string): string {
  return formatDateLocale(new Date(Date.parse(windowEnd) - 1));
}

export function VoiceThemeProposals({ onOpenCall }: VoiceThemeProposalsProps) {
  const { t } = useTranslation(['dashboard']);
  const query = useThemeProposals({ enabled: true });
  const decide = useDecideThemeProposal();
  // Several proposals can be in flight at once: finishing one must not
  // re-enable the buttons of another that is still deciding.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  // A failed list says why (the flag, the plan, a narrow grant) — read once
  // per error from its body, not on every render.
  useEffect(() => {
    if (!query.error) {
      setLoadError(null);
      return;
    }
    let live = true;
    void proposalErrorMessage(
      query.error,
      t,
      t('voicePage.themes.proposals.loadFailed')
    ).then((message) => {
      if (live) setLoadError(message);
    });
    return () => {
      live = false;
    };
  }, [query.error, t]);

  const run = async (proposal: OrgThemeProposal, decision: ThemeProposalDecision) => {
    setPendingIds((current) => new Set(current).add(proposal.id));
    setErrors((current) => {
      const { [proposal.id]: _dropped, ...rest } = current;
      return rest;
    });
    try {
      await decide.mutateAsync({ id: proposal.id, decision });
    } catch (error) {
      const message = await proposalErrorMessage(
        error,
        t,
        t('voicePage.themes.proposals.errors.generic')
      );
      setErrors((current) => ({ ...current, [proposal.id]: message }));
    } finally {
      setPendingIds((current) => {
        const next = new Set(current);
        next.delete(proposal.id);
        return next;
      });
    }
  };

  const body = (() => {
    if (query.isPending) {
      return (
        <p className="text-sm text-fg-muted" role="status">
          {t('voicePage.themes.proposals.loading')}
        </p>
      );
    }
    if (!query.data) {
      return (
        <div className="space-y-2" role="alert">
          <p className="text-sm text-danger">
            {loadError ?? t('voicePage.themes.proposals.loadFailed')}
          </p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('voicePage.themes.retry')}
          </Button>
        </div>
      );
    }
    if (query.data.proposals.length === 0) {
      return (
        <p className="text-sm text-fg-muted">{t('voicePage.themes.proposals.empty')}</p>
      );
    }
    return (
      <ul className="space-y-3">
        {query.data.proposals.map((proposal) => {
          const busy = pendingIds.has(proposal.id);
          const accepting = proposal.status === 'accepting';
          return (
            <li
              key={proposal.id}
              className="space-y-2 rounded-card border border-border bg-bg-elevated px-3 py-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium text-fg">{proposal.name}</p>
                {accepting && (
                  <span className="text-xs text-fg-muted" role="status">
                    {t('voicePage.themes.proposals.accepting')}
                  </span>
                )}
              </div>
              <p className="text-sm text-fg-muted">
                {proposal.needsDefinition || proposal.definition === ''
                  ? t('voicePage.themes.proposals.needsDefinition')
                  : proposal.definition}
              </p>
              <p className="text-xs text-fg-muted">
                {t('voicePage.themes.proposals.share', {
                  calls: proposal.calls,
                  percent: sharePercent(proposal.share),
                  start: formatDateLocale(proposal.windowStart),
                  end: lastIncludedDay(proposal.windowEnd),
                })}
              </p>
              {proposal.examples.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
                  <span>{t('voicePage.themes.proposals.examples')}</span>
                  {proposal.examples.map((example) =>
                    onOpenCall ? (
                      <button
                        key={example.recordId}
                        type="button"
                        className="font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => onOpenCall(example.recordId)}
                      >
                        {t('voicePage.themes.proposals.example', {
                          date: formatDateLocale(example.createdAt),
                        })}
                      </button>
                    ) : (
                      <span key={example.recordId}>
                        {t('voicePage.themes.proposals.example', {
                          date: formatDateLocale(example.createdAt),
                        })}
                      </span>
                    )
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => void run(proposal, 'accept')}
                >
                  {t('voicePage.themes.proposals.accept')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || accepting}
                  onClick={() => void run(proposal, 'reject')}
                >
                  {t('voicePage.themes.proposals.reject')}
                </Button>
              </div>
              {errors[proposal.id] && (
                <p role="alert" className="text-sm text-danger">
                  {errors[proposal.id]}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    );
  })();

  return (
    <section
      className="space-y-3 rounded-card border border-border bg-bg-surface px-4 py-4"
      aria-labelledby="voice-themes-proposals"
    >
      <h3 id="voice-themes-proposals" className="text-sm font-semibold text-fg">
        {t('voicePage.themes.proposals.title')}
      </h3>
      <p className="text-sm text-fg-muted">
        {t('voicePage.themes.proposals.description')}
      </p>
      {body}
    </section>
  );
}
